import React, {useState, forwardRef} from 'react'
import {Box, Transition, Tooltip, TransitionOverride} from '@mantine/core'
import {useSliderContext} from './slider'
import {WrapIf} from 'shared/ui'

export type ThumbShape = 'circle' | 'rectangle' | 'square' | 'custom'

export interface ThumbProps {
  max: number
  min: number
  value: number
  position: number
  dragging: boolean
  label: React.ReactNode
  onKeyDownCapture?(event: React.KeyboardEvent<HTMLDivElement>): void
  onMouseDown?(event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>): void
  labelTransitionProps: TransitionOverride | undefined
  labelAlwaysOn: boolean | undefined
  onFocus?(): void
  onBlur?(): void
  showLabelOnHover: boolean | undefined
  isHovered?: boolean
  children?: React.ReactNode
  disabled: boolean | undefined
  className?: string
  style?: React.CSSProperties
  shape: ThumbShape
  tip?: React.ReactNode
  name: string
  thumbSize?: number
  hideOutOfRange?: boolean
}

export const Thumb = forwardRef<HTMLDivElement, ThumbProps>(
  (
    {
      max,
      min,
      value,
      position,
      label,
      dragging,
      onMouseDown,
      onKeyDownCapture,
      labelTransitionProps,
      labelAlwaysOn,
      onFocus,
      onBlur,
      showLabelOnHover,
      isHovered,
      children = null,
      disabled,
      shape,
      tip,
      name,
      className,
      thumbSize = 20,
      hideOutOfRange,
    }: ThumbProps,
    ref
  ) => {
    const {getStyles} = useSliderContext()
    const [focused, setFocused] = useState(false)
    const isVisible = labelAlwaysOn || dragging || focused || (showLabelOnHover && isHovered)
    
    const isOutOfRange = hideOutOfRange && (value < min || value > max)
    if (isOutOfRange) {
      return null
    }

    return (
      <WrapIf
        if={tip != null}
        wrap={c => (
          <Tooltip withinPortal withArrow position='bottom' offset={20} label={tip}>
            {c}
          </Tooltip>
        )}>
        <Box<'div'>
          tabIndex={0}
          role='slider'
          aria-valuemax={max}
          aria-valuemin={min}
          aria-valuenow={value}
          ref={ref as any}
          __vars={{'--slider-thumb-offset': `${position}%`, '--slider-thumb-size': `${thumbSize}px`}}
          {...getStyles('thumb', {focusable: true, className})}
          mod={{dragging, disabled, shape}}
          onFocus={() => {
            setFocused(true)
            typeof onFocus === 'function' && onFocus()
          }}
          onBlur={() => {
            setFocused(false)
            typeof onBlur === 'function' && onBlur()
          }}
          onTouchStart={onMouseDown}
          onMouseDown={onMouseDown}
          onKeyDownCapture={onKeyDownCapture}
          onClick={event => event.stopPropagation()}>
          {children}
          <Transition mounted={label != null && !!isVisible} transition='fade' duration={0} {...labelTransitionProps}>
            {transitionStyles => (
              <Box
                {...getStyles('label', {
                  style: {...transitionStyles}
                })}>
                {label}
              </Box>
            )}
          </Transition>
        </Box>
      </WrapIf>
    )
  }
)

//@ts-ignore
Thumb.displayName = '@mantine/core/SliderThumb'
